import React, {useState, useEffect, useRef, Fragment} from 'react';
import {createPortal} from 'react-dom';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';

import {getCookie, removeCookie} from '../../api/cookie';
import {useDocumentViewer} from '../../context/DocumentViewerContext';
import {readHistoryList, updateHistoryInfo, clearDeleteResponses} from '../../modules/chat/chat';
import getRelativeDateFromToday from '../../utils/datetimer';
import validateInput from '../../utils/validateInput';

import {HiPlus, HiOutlineDotsVertical} from "react-icons/hi";
import styles from './styles/userLayout.module.css';

import RemoveHistoryModal from '../chat/RemoveHistoryModal';

import {ReactComponent as Logo} from '../../assets/logo/logo-full-H22.svg';
import {ReactComponent as HamburgerIcon} from '../../assets/design-system/hamburger.svg';
import { ReactComponent as PinnedHistory } from '../../assets/Icon_pinned.svg';
import { ReactComponent as HistoryPin } from '../../assets/menu/Icon_pin.svg';
import { ReactComponent as HistoryUnPin } from '../../assets/menu/Icon_unpin.svg';
import { ReactComponent as HistoryTitle } from '../../assets/menu/Icon_pencil.svg';
import { ReactComponent as HistoryRemove } from '../../assets/menu/Icon_trash.svg';
import { ReactComponent as UserAdmin } from '../../assets/menu/Icon_admin.svg';
import { ReactComponent as UserFeedback } from '../../assets/menu/Icon_feed_user.svg';
import { ReactComponent as UserLogout } from '../../assets/menu/Icon_logout.svg';
import { ReactComponent as HistoryPinActive } from '../../assets/menu/Icon_pin_a.svg';
import { ReactComponent as HistoryUnPinActive } from '../../assets/menu/Icon_unpin_a.svg';
import { ReactComponent as HistoryTitleActive } from '../../assets/menu/Icon_pencil_a.svg';
import { ReactComponent as HistoryRemoveActive } from '../../assets/menu/Icon_trash_a.svg';
import { ReactComponent as UserAdminActive } from '../../assets/menu/Icon_admin_a.svg';
import { ReactComponent as UserFeedbackActive } from '../../assets/menu/Icon_feed_user_a.svg';
import { ReactComponent as UserLogoutActive } from '../../assets/menu/logout-active.svg';
import { ReactComponent as IconError } from '../../assets/Icon_error.svg';
import {ReactComponent as ArrowRight} from '../../assets/side-bar/arrow-right.svg';

export default function UserLayout() {
    const { closeRightSideBar } = useDocumentViewer();

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [activePath, setActivePath] = useState(location.pathname);
    const inputTitleRefsPinned = useRef([]);
    const inputTitleRefsUnPinned = useRef([]);

    const accessToken = getCookie('accessToken');
    const username = getCookie('username');
    const isAdmin = getCookie('administrator');

    const { listResponse, listErrorResponse, updateResponse, updatePinErrorResponse, updateTitleErrorResponse, deleteResponse, deleteErrorResponse } = useSelector(({ chat, loading }) => ({
        listResponse: chat.listResponse,
        listErrorResponse: chat.listErrorResponse,
        updateResponse: chat.updateResponse,
        updatePinErrorResponse: chat.updatePinErrorResponse,
        updateTitleErrorResponse: chat.updateTitleErrorResponse,
        deleteResponse: chat.deleteResponse,
        deleteErrorResponse: chat.deleteErrorResponse,
    }));

    const [isOpenSidebar, setIsOpenSidebar] = useState(true);
    const [isOpenPopup, setIsOpenPopup] = useState(false);
    const [isOpenHistoryPopup, setIsOpenHistoryPopup] = useState(false);
    const [isOpenHistoryErrorPopup, setIsOpenHistoryErrorPopup] = useState(false);
  
    const [chatHistories, setChatHistories] = useState([]);
    const [pinnedChatHistories, setPinnedChatHistories] = useState([]);
    const [unpinnedChatHistories, setUnpinnedChatHistories] = useState([]);
    const usedDateGroups = new Set();

    const [hoveredUserMenuIndex, setHoveredUserMenuIndex] = useState(null);
    const [hoveredHistoryMenuIndex, setHoveredHistoryMenuIndex] = useState(null);
    const [openedHistoryContextIndex, setOpenedHistoryContextIndex] = useState(null);
    const [hoveredHistoryIndex, setHoveredHistoryIndex] = useState(null);

    const [hoveredPinnedHistoryMenuIndex, setHoveredPinnedHistoryMenuIndex] = useState(null);
    const [openedPinnedHistoryContextIndex, setOpenedPinnedHistoryContextIndex] = useState(null);
    const [hoveredPinnedHistoryIndex, setHoveredPinnedHistoryIndex] = useState(null);

    const [isOpenRemoveHistoryModal, setIsOpenRemoveHistoryModal] = useState(false);
    const [historyIdToRemove, setHistoryIdToRemove] = useState(null);

    const [isModifyHistoryTitle, setIsModifyHistoryTitle] = useState(false);
    const [selectedHistoryTitleOriginal, setSelectedHistoryTitleOriginal] = useState(null);
    const [selectedHistoryTitle, setSelectedHistoryTitle] = useState(null);
    const [selectedModifyHistoryTitleId, setSelectedModifyHistoryTitleId] = useState(null);

    const [popupPosition, setPopupPosition] = useState(null);

    const handleMouseEnter = (idx, isPinned) => {
        if (!isPinned) {
            setHoveredHistoryIndex(idx);
            setHoveredPinnedHistoryIndex(null);
        } else {
            setHoveredPinnedHistoryIndex(idx);
            setHoveredHistoryIndex(null);
        }
    };
    const handleMouseLeave = (isPinned) => {
        if (!isPinned) setHoveredHistoryIndex(null);
        else setHoveredPinnedHistoryIndex(null);
    };

    const handleSidebar = () => {
        setIsOpenSidebar(!isOpenSidebar);

        if (!isOpenSidebar) setIsOpenPopup(false);
    };

    const handleNavigate = (path) => {
        navigate(path);
    };

    const logout = () => {
        removeCookie('accessToken', { path: '/' });
        removeCookie('username', { path: '/' });
        removeCookie('administrator', { path: '/' });
        navigate('/auth/login');
    };

    const handleClickNewChat = () => {
        if (location.pathname === '/chat') {
            navigate(0);
        } else {
            navigate('/chat');
        }
        closeRightSideBar();
        dispatch(readHistoryList());
    };

    const handleClickHistoryItem = (historyId, idx) => {
        if (activePath !== historyId && !isOpenHistoryPopup) {
            closeRightSideBar();
            navigate(`/chat/${historyId}`);
            dispatch(readHistoryList());
        }
    };

    const handleClickOpenContextHistory = (e, idx, isPinned) => {
        const mouseY = e.clientY;
        setPopupPosition(mouseY - 65);  // center aligned

        if (!isPinned) {
            setOpenedHistoryContextIndex(idx);
            setOpenedPinnedHistoryContextIndex(null);
        } else {
            setOpenedPinnedHistoryContextIndex(idx);
            setOpenedHistoryContextIndex(null);
        }
        setIsOpenHistoryPopup(!isOpenHistoryPopup);
    };

    const handleClickWrapper = () => {
        if (isOpenHistoryPopup) setIsOpenHistoryPopup(false);
        if (isOpenPopup) setIsOpenPopup(false);
        if (isOpenHistoryErrorPopup) setIsOpenHistoryErrorPopup(false);
        if (isModifyHistoryTitle) { 
            setIsModifyHistoryTitle(false);
            setHoveredHistoryMenuIndex(null);
            setHoveredPinnedHistoryMenuIndex(null);    
        }
        setHoveredUserMenuIndex(null);
    };

    const readCurrentPath = () => {
        const id = location.pathname.replace('/chat/', '');
        setActivePath(id);
    };

    const handleMouseEnterOnMenu = (idx, isPinned) => {
        if (!isPinned) setHoveredHistoryMenuIndex(idx);
        else setHoveredPinnedHistoryMenuIndex(idx);
    };
    const handleMouseLeaveOnMenu = (isPinned) => {
        if (!isPinned) setHoveredHistoryMenuIndex(null);
        else setHoveredPinnedHistoryMenuIndex(null);
    };
    const handleMouseEnterOnUserMenu = (idx) => {
        setHoveredUserMenuIndex(idx);
    };
    const handleMouseLeaveOnUserMenu = () => {
        setHoveredUserMenuIndex(null);
    };

    const handleClickModifyHistory = (id, index, title, category) => {
        setIsModifyHistoryTitle(true);
        setSelectedModifyHistoryTitleId(id);
        setSelectedHistoryTitleOriginal(title);
        setSelectedHistoryTitle(title);
        setHoveredHistoryMenuIndex(null);
        setHoveredPinnedHistoryMenuIndex(null);

        setTimeout(() => {
            if (category === 'UNPIN') {
                if (inputTitleRefsUnPinned.current[index]) {
                    inputTitleRefsUnPinned.current[index].focus();
                    inputTitleRefsUnPinned.current[index].select();    
                }    
            } else {
                if (inputTitleRefsPinned.current[index]) {
                    inputTitleRefsPinned.current[index].focus();
                    inputTitleRefsPinned.current[index].select();    
                }
            }
        }, 50);
    };
    const handleClickModifyHistoryPin = (id) => {
        dispatch(updateHistoryInfo({id, data: {isPinned: true}}));
        setHoveredHistoryMenuIndex(null);
        setHoveredPinnedHistoryMenuIndex(null);
    };
    const handleClickModifyHistoryUnPin = (id) => {
        dispatch(updateHistoryInfo({id, data: {isPinned: false}}));
        setHoveredHistoryMenuIndex(null);
        setHoveredPinnedHistoryMenuIndex(null);
    };
    const handleSetIsOpenRemoveHistoryModal = (status) => {
        setIsOpenRemoveHistoryModal(status);
        if (!status) {
            if (deleteResponse !== null) {
                if (activePath * 1 === historyIdToRemove) navigate('/chat');                    
            }
            setHistoryIdToRemove(null);
            dispatch(clearDeleteResponses());
        }
    };
    const handleOverlayRemoveHistory = (e) => {
        if (e.target === e.currentTarget) {
            setIsOpenRemoveHistoryModal(false);
        }
    };
    const handleClickDeleteHistory = (id) => {
        setHistoryIdToRemove(id);
        setIsOpenRemoveHistoryModal(true);
        setHoveredHistoryMenuIndex(null);
        setHoveredPinnedHistoryMenuIndex(null);
    };
    const handleChangeInputSelectedHistoryTitle = (e) => {
        setSelectedHistoryTitle(e.target.value);
    };
    const handleKeyDownModifyHistoryTitle = async (e, id) => {
        if (e.key === 'Enter') {
            const trimmed = selectedHistoryTitle.trim();
            if (!trimmed) {
                setSelectedHistoryTitle(null);
                setIsModifyHistoryTitle(false);
                return;
            }
            if (!validateInput(trimmed)) {
                setSelectedHistoryTitle(null);
                setIsModifyHistoryTitle(false);
                alert("입력 값에 허용되지 않는 특수문자 또는 위험한 코드가 포함되어 있습니다.");
                return;
            }
            dispatch(updateHistoryInfo({id, data: {title: selectedHistoryTitle}}));
        }
    };

    const handleClickBlurDiv = (e) => {
        e.target.parentElement.scrollTop += 30;
    };

    useEffect(() => {
        if (updateResponse) {
            setIsModifyHistoryTitle(false);
            setHoveredHistoryMenuIndex(null);
            setHoveredPinnedHistoryMenuIndex(null);    
            dispatch(clearDeleteResponses());
        }
        if (updateTitleErrorResponse) {
            setIsModifyHistoryTitle(true);
        }
    }, [updateResponse, updateTitleErrorResponse]);

    useEffect(() => {
        if (!location.pathname) return;
        readCurrentPath();
    }, [location.pathname]);

    useEffect(() => {
        if (!chatHistories) return;
        setUnpinnedChatHistories(chatHistories.filter(x => !x.isPinned));
        setPinnedChatHistories(chatHistories.filter(x => x.isPinned));
    }, [chatHistories]);

    useEffect(() => {
        setChatHistories(listResponse);
    }, [listResponse]);

    useEffect(() => {
        if (updatePinErrorResponse) setIsOpenHistoryErrorPopup(true);
        if (updateTitleErrorResponse) setSelectedHistoryTitle(selectedHistoryTitleOriginal);
    }, [updatePinErrorResponse, updateTitleErrorResponse]);

    useEffect(() => {
        if (location.pathname) readCurrentPath();
        dispatch(readHistoryList());
    }, []);

    // boxPopup 외 영역 클릭 시 닫기
    useEffect(() => {
        const handleDocumentClick = (e) => {
            // 팝업 내부 클릭은 무시
            const isInsidePopup = e.target.closest(`[class*="boxPopup"]`);
            if (isInsidePopup) {
                return;
            }
            
            // 사용자 영역 클릭은 무시 (팝업 토글용)
            const isInsideUserArea = e.target.closest(`[class*="boxUser"]`);
            if (isInsideUserArea) {
                return;
            }
            
            // 바깥쪽 클릭 시 팝업 닫기
            if (isOpenPopup) {
                setIsOpenPopup(false);
                setHoveredUserMenuIndex(null);
            }
        };

        // ESC 키 이벤트
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpenPopup) {
                setIsOpenPopup(false);
                setHoveredUserMenuIndex(null);
            }
        };

        if (isOpenPopup) {
            document.addEventListener('click', handleDocumentClick);
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpenPopup]);

    return (
        <div className={styles.wrapper} onClick={handleClickWrapper}>
            {isOpenRemoveHistoryModal && (
                    <div className={styles.modalOverlay} onClick={handleOverlayRemoveHistory}>
                        <div onClick={(e) => e.stopPropagation()}>
                            <RemoveHistoryModal isOpenModal={isOpenRemoveHistoryModal} setIsOpenModal={handleSetIsOpenRemoveHistoryModal} id={historyIdToRemove} />
                        </div>
                    </div>
                )}
            {isOpenSidebar
                ? <div className={`${styles.container} ${styles.containerWide}`}>
                    <div>
                        <div className={styles.boxLogo}>
                            <HamburgerIcon
                                className={styles.iconHamburger}
                                onClick={handleSidebar}
                            />
                            <Link to='/chat'><Logo/></Link>
                        </div>
                        {<div className={styles.boxMenu}>
                                <div className={styles.btnNewChat} onClick={handleClickNewChat}>
                                    <HiPlus/>
                                    <span>새 대화</span>
                                </div>
                                {listErrorResponse ?
                                    <div className={styles.errorHistoryLoad}>
                                        <IconError style={{width: '20px', height: '20px'}}/>
                                        <div>대화내역을 불러오는 중&nbsp;<span>오류</span>&nbsp;발생</div>
                                        <p>잠시 후 다시 시도해 주세요.</p>
                                    </div>
                                    : 
                                    ((unpinnedChatHistories && unpinnedChatHistories.length > 0) || (pinnedChatHistories && pinnedChatHistories.length > 0)) ?
                                    <div className={styles.historyListContainer}>
                                    {pinnedChatHistories && pinnedChatHistories.length > 0 ?
                                        <div className={styles.pinnedHistoryContainer}>
                                        {pinnedChatHistories.map((history, index) => {
                                            const isActive = `${history.id}` === activePath;
                                                return (
                                                    <div key={`history-${index}`}>
                                                        {openedPinnedHistoryContextIndex === index && isOpenHistoryPopup && <div className={styles.boxPopupHistory} style={popupPosition !== null ? {top: popupPosition} : {}}>
                                                                <div className={styles.popMenuHistory} onMouseEnter={e => handleMouseEnterOnMenu(0, true)} onMouseLeave={e => handleMouseLeaveOnMenu(true)} onClick={e => handleClickModifyHistoryUnPin(history.id)}>
                                                                    {hoveredPinnedHistoryMenuIndex === 0 ? <HistoryUnPinActive/> : <HistoryUnPin/>}
                                                                    <p>고정 해제</p>
                                                                </div>
                                                                <div className={styles.popMenuHistory} onMouseEnter={e => handleMouseEnterOnMenu(1, true)} onMouseLeave={e => handleMouseLeaveOnMenu(true)} onClick={e => handleClickModifyHistory(history.id, index, history.title, 'PIN')}>
                                                                    {hoveredPinnedHistoryMenuIndex === 1 ? <HistoryTitleActive/> : <HistoryTitle/>}
                                                                    <p>제목 변경</p>
                                                                </div>
                                                                <div className={styles.popMenuHistory} onMouseEnter={e => handleMouseEnterOnMenu(2, true)} onMouseLeave={e => handleMouseLeaveOnMenu(true)} onClick={e => handleClickDeleteHistory(history.id)}>
                                                                    {hoveredPinnedHistoryMenuIndex === 2 ? <HistoryRemoveActive/> : <HistoryRemove/>}
                                                                    <p>삭제</p>
                                                                </div>
                                                            </div>}
                                                        {isOpenHistoryErrorPopup && openedPinnedHistoryContextIndex === index && !isOpenHistoryPopup?
                                                            <div className={styles.boxPopupHistoryFailure}>
                                                                <div className={styles.popMenuHistory}>
                                                                    <IconError style={{width: '20px', height: '20px'}}/>
                                                                    <p>서버 오류로 인한 고정 해제 실패</p>
                                                                </div>
                                                            </div>
                                                            : null
                                                        }
                                                        {isModifyHistoryTitle && selectedModifyHistoryTitleId === history.id ?
                                                            <div className={styles.modifyHistoryTitleArea}>
                                                                <input type='text' ref={(el) => inputTitleRefsPinned.current[index] = el}
                                                                    value={selectedHistoryTitle !== null ? selectedHistoryTitle : history.title}
                                                                    onChange={handleChangeInputSelectedHistoryTitle}
                                                                    onKeyDown={e => handleKeyDownModifyHistoryTitle(e, history.id)}
                                                                    onClick={e => e.stopPropagation()} // 이벤트 전파 방지
                                                                    autoComplete='off'
                                                                ></input>
                                                            </div>
                                                            :
                                                            <div className={isActive ? `${styles.menu} ${styles.menuActive}` : styles.menu} onClick={e => handleClickHistoryItem(history.id, index)} onMouseEnter={e => handleMouseEnter(index, true)} onMouseLeave={e => handleMouseLeave(true)}>
                                                                <div className={styles.pinnedIconWrapper}><PinnedHistory/></div>
                                                                <p style={{marginLeft: '18px'}} className={styles.pinnedHistoryTitle}>{history.title}</p>
                                                                {hoveredPinnedHistoryIndex === index &&
                                                                    <div className={styles.contextIconWrapper} onClick={e => handleClickOpenContextHistory(e, index, true)}>
                                                                        <HiOutlineDotsVertical style={{color: 'var(--gray-dark)'}}/>
                                                                    </div>}
                                                            </div>
                                                        }
                                                    </div>
                                                );
                                            })
                                        }
                                        {pinnedChatHistories.length > 4 ? <div className={styles.effectBlurDiv} onClick={handleClickBlurDiv}>
                                            </div> : null}
                                        {pinnedChatHistories.length > 4 ? <div className={styles.emptyDivToAddScroll}></div> : null
                                        }
                                    </div> : null}
                                    <div className={styles.historyContainer} style={!pinnedChatHistories || pinnedChatHistories.length < 1 ? {maxHeight: 'calc(100vh - 220px)'} : 
                                                                                    pinnedChatHistories.length === 1 ? {minHeight: 'calc(100vh - 450px)', maxHeight: 'calc(100vh - 295px)'} : 
                                                                                    pinnedChatHistories.length === 2 ? {minHeight: 'calc(100vh - 450px)', maxHeight: 'calc(100vh - 325px)'} : 
                                                                                    pinnedChatHistories.length === 3 ? {minHeight: 'calc(100vh - 450px)', maxHeight: 'calc(100vh - 350px)'} : 
                                                                                    pinnedChatHistories.length === 4 ? {minHeight: 'calc(100vh - 450px)', maxHeight: 'calc(100vh - 400px)'} : 
                                                                                    {height: 'calc(100vh - 450px)'}}>
                                        {unpinnedChatHistories.map((history, index) => {
                                            const isActive = `${history.id}` === activePath;
                                            const relativeDate = getRelativeDateFromToday(history.updatedAt);
                                            if (!usedDateGroups.has(relativeDate)) {
                                                usedDateGroups.add(relativeDate);
                                                return (
                                                    <div key={`history-${index}`} style={unpinnedChatHistories.length - 1 === index ? {margin: '28px 0'} : index > 0 ? {marginTop: '28px'} : {}}>
                                                        <p className={styles.historyGroup}>{relativeDate}</p>
                                                        {openedHistoryContextIndex === index && isOpenHistoryPopup && <div className={styles.boxPopupHistory} style={popupPosition !== null ? {top: popupPosition} : {}}>
                                                                <div className={styles.popMenuHistory} onMouseEnter={e => handleMouseEnterOnMenu(0, false)} onMouseLeave={e => handleMouseLeaveOnMenu(false)} onClick={e => handleClickModifyHistoryPin(history.id)}>
                                                                    {hoveredHistoryMenuIndex === 0 ? <HistoryPinActive/> : <HistoryPin/>}
                                                                    <p>고정</p>
                                                                </div>
                                                                <div className={styles.popMenuHistory} onMouseEnter={e => handleMouseEnterOnMenu(1, false)} onMouseLeave={e => handleMouseLeaveOnMenu(false)} onClick={e => handleClickModifyHistory(history.id, index, history.title, 'UNPIN')}>
                                                                    {hoveredHistoryMenuIndex === 1 ? <HistoryTitleActive/> : <HistoryTitle/>}
                                                                    <p>제목 변경</p>
                                                                </div>
                                                                <div className={styles.popMenuHistory} onMouseEnter={e => handleMouseEnterOnMenu(2, false)} onMouseLeave={e => handleMouseLeaveOnMenu(false)} onClick={e => handleClickDeleteHistory(history.id)}>
                                                                    {hoveredHistoryMenuIndex === 2 ? <HistoryRemoveActive/> : <HistoryRemove/>}
                                                                    <p>삭제</p>
                                                                </div>
                                                            </div>}
                                                        {isOpenHistoryErrorPopup && openedHistoryContextIndex === index && !isOpenHistoryPopup ?
                                                            <div className={styles.boxPopupHistoryFailure}>
                                                                <div className={styles.popMenuHistory}>
                                                                    <IconError style={{width: '20px', height: '20px'}}/>
                                                                    <p>서버 오류로 인한 고정 실패</p>
                                                                </div>
                                                            </div>
                                                            : null
                                                        }
                                                        {isModifyHistoryTitle && selectedModifyHistoryTitleId === history.id ?
                                                            <div className={styles.modifyHistoryTitleArea}>
                                                                <input type='text' ref={(el) => inputTitleRefsUnPinned.current[index] = el}
                                                                    value={selectedHistoryTitle !== null ? selectedHistoryTitle : history.title}
                                                                    onChange={handleChangeInputSelectedHistoryTitle}
                                                                    onKeyDown={e => handleKeyDownModifyHistoryTitle(e, history.id)}
                                                                    onClick={e => e.stopPropagation()} // 이벤트 전파 방지
                                                                    autoComplete='off'
                                                                ></input>
                                                            </div>
                                                            :
                                                            <div className={isActive ? `${styles.menu} ${styles.menuActive}` : styles.menu} onClick={e => handleClickHistoryItem(history.id, index)} onMouseEnter={e => handleMouseEnter(index, false)} onMouseLeave={e => handleMouseLeave(false)}>
                                                                <p className={styles.historyTitle}>{history.title}</p>
                                                                {hoveredHistoryIndex === index &&
                                                                    <div className={styles.contextIconWrapper} onClick={e => handleClickOpenContextHistory(e, index, false)}>
                                                                        <HiOutlineDotsVertical style={{color: 'var(--gray-dark)'}}/>
                                                                    </div>}
                                                            </div>
                                                        }
                                                    </div>
                                                );
                                            } else {
                                                return (
                                                    <div key={`history-${index}`} className={styles.historyItem} style={unpinnedChatHistories.length - 1 === index ? {marginBottom: '28px'} : {}}>
                                                        {openedHistoryContextIndex === index && isOpenHistoryPopup && <div className={styles.boxPopupHistory} style={popupPosition !== null ? {top: popupPosition} : {}}>
                                                                <div className={styles.popMenuHistory} onMouseEnter={e => handleMouseEnterOnMenu(0, false)} onMouseLeave={e => handleMouseLeaveOnMenu(false)} onClick={e => handleClickModifyHistoryPin(history.id)}>
                                                                    {hoveredHistoryMenuIndex === 0 ? <HistoryPinActive/> : <HistoryPin/>}
                                                                    <p>고정</p>
                                                                </div>
                                                                <div className={styles.popMenuHistory} onMouseEnter={e => handleMouseEnterOnMenu(1, false)} onMouseLeave={e => handleMouseLeaveOnMenu(false)} onClick={e => handleClickModifyHistory(history.id, index, history.title, 'UNPIN')}>
                                                                    {hoveredHistoryMenuIndex === 1 ? <HistoryTitleActive/> : <HistoryTitle/>}
                                                                    <p>제목 변경</p>
                                                                </div>
                                                                <div className={styles.popMenuHistory} onMouseEnter={e => handleMouseEnterOnMenu(2, false)} onMouseLeave={e => handleMouseLeaveOnMenu(false)} onClick={e => handleClickDeleteHistory(history.id)}>
                                                                    {hoveredHistoryMenuIndex === 2 ? <HistoryRemoveActive/> : <HistoryRemove/>}
                                                                    <p>삭제</p>
                                                                </div>
                                                            </div>}
                                                        {isOpenHistoryErrorPopup && openedHistoryContextIndex === index && !isOpenHistoryPopup ?
                                                            <div className={styles.boxPopupHistoryFailure}>
                                                                <div className={styles.popMenuHistory}>
                                                                    <IconError style={{width: '20px', height: '20px'}}/>
                                                                    <p>서버 오류로 인한 고정 실패</p>
                                                                </div>
                                                            </div>
                                                            : null
                                                        }
                                                        {isModifyHistoryTitle && selectedModifyHistoryTitleId === history.id ?
                                                            <div className={styles.modifyHistoryTitleArea}>
                                                                <input type='text' ref={(el) => inputTitleRefsUnPinned.current[index] = el}
                                                                    value={selectedHistoryTitle !== null ? selectedHistoryTitle : history.title}
                                                                    onChange={handleChangeInputSelectedHistoryTitle}
                                                                    onKeyDown={e => handleKeyDownModifyHistoryTitle(e, history.id)}
                                                                    onClick={e => e.stopPropagation()} // 이벤트 전파 방지
                                                                    autoComplete='off'
                                                                ></input>
                                                            </div>
                                                            :
                                                            <div className={isActive ? `${styles.menu} ${styles.menuActive}` : styles.menu} onClick={e => handleClickHistoryItem(history.id, index)} onMouseEnter={e => handleMouseEnter(index, false)} onMouseLeave={e => handleMouseLeave(false)}>
                                                                <p className={styles.historyTitle}>{history.title}</p>
                                                                {hoveredHistoryIndex === index &&
                                                                    <div className={styles.contextIconWrapper} onClick={e => handleClickOpenContextHistory(e, index, false)}>
                                                                        <HiOutlineDotsVertical style={{color: 'var(--gray-dark)'}}/>
                                                                    </div>}
                                                            </div>
                                                        }
                                                    </div>
                                                );
                                            }
                                        })}
                                    </div>
                                    </div>
                                    :
                                    <div className={styles.initialHistoryLoad}>
                                        <div>질문을 통해<br/>대화를 시작해 보세요.</div>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                    <div>
                        {accessToken
                            ? 
                            <>
                                <div className={styles.boxUser} onClick={() => setIsOpenPopup(!isOpenPopup)}>
                                    <p>{username}님</p>
                                    <ArrowRight />
                                </div>
                                {username && isOpenPopup
                                    ? createPortal(
                                        <div className={styles.boxPopup}>
                                            {isAdmin
                                                ?  <div className={styles.popMenu} 
                                                    onClick={() => handleNavigate('/admin')}
                                                    onMouseEnter={e => handleMouseEnterOnUserMenu(0)}
                                                    onMouseLeave={handleMouseLeaveOnUserMenu}
                                                >
                                                    {hoveredUserMenuIndex === 0 ? <UserAdminActive/> : <UserAdmin/>}
                                                <Link className={styles.link} to='/admin/feedback'>관리자 모드</Link>
                                                </div>
                                                : null
                                            }
                                            {/* <div className={styles.popMenu}
                                                 onClick={() => handleNavigate('/feedforward')}
                                                 onMouseEnter={e => handleMouseEnterOnUserMenu(1)}
                                                 onMouseLeave={handleMouseLeaveOnUserMenu}
                                            >
                                                {hoveredUserMenuIndex === 1 ? <UserFeedbackActive/> : <UserFeedback/>}
                                                <Link className={styles.link} to='/feedforward'>MY 피드백</Link>
                                            </div> */}
                                            <div className={styles.popMenu}
                                                 onClick={logout}
                                                 onMouseEnter={e => handleMouseEnterOnUserMenu(2)}
                                                 onMouseLeave={handleMouseLeaveOnUserMenu}
                                            >
                                                {hoveredUserMenuIndex === 2 ? <UserLogoutActive/> : <UserLogout/>}
                                                <p>로그아웃</p>
                                            </div>
                                        </div>,
                                        document.body
                                    )
                                    : null
                                }
                            </>
                            : null
                        }
                    </div>
                </div>
                : <div className={styles.container}>
                    <div>
                        <div className={styles.boxLogoSmall}>
                            <HamburgerIcon
                                className={styles.iconHamburger}
                                onClick={handleSidebar}
                            />
                        </div>
                        <div className={styles.boxMenu}>
                            <div className={styles.btnNewChatFolded} onClick={handleClickNewChat}>
                                <HiPlus/>
                            </div>
                        </div>
                    </div>
                    <div>
                        {accessToken
                            ? <>
                                <div className={styles.boxUserFolded} onClick={() => setIsOpenPopup(!isOpenPopup)}>
                                    <ArrowRight />
                                </div>
                                {username && isOpenPopup
                                    ? createPortal(
                                        <div className={styles.boxPopupFolded}>
                                            {isAdmin
                                                ? <div className={styles.popMenu}
                                                    onClick={() => handleNavigate('/admin')}
                                                    onMouseEnter={e => handleMouseEnterOnUserMenu(0)}
                                                    onMouseLeave={handleMouseLeaveOnUserMenu}
                                                >
                                                {hoveredUserMenuIndex === 0 ? <UserAdminActive/> : <UserAdmin/>}
                                                 <Link className={styles.link} to='/admin/feedback'>관리자 모드</Link>
                                                </div>
                                                : null
                                            }
                                            {/* <div className={styles.popMenu}
                                                 onClick={() => handleNavigate('/feedforward')}
                                                 onMouseEnter={e => handleMouseEnterOnUserMenu(1)}
                                                 onMouseLeave={handleMouseLeaveOnUserMenu}
                                            >
                                                {hoveredUserMenuIndex === 1 ? <UserFeedbackActive/> : <UserFeedback/>}
                                                <Link className={styles.link} to='/feedforward'>MY 피드백</Link>
                                            </div> */}
                                            <div className={styles.popMenu}
                                                 onClick={logout}
                                                 onMouseEnter={e => handleMouseEnterOnUserMenu(2)}
                                                 onMouseLeave={handleMouseLeaveOnUserMenu}
                                            >
                                                {hoveredUserMenuIndex === 2 ? <UserLogoutActive/> : <UserLogout/>}
                                                <p>로그아웃</p>
                                            </div>
                                        </div>,
                                        document.body
                                    )
                                    : null
                                }
                            </>
                            : null
                        }
                    </div>
                </div>
            }
        </div>
    );
}
